import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { notificationsAPI } from '../lib/api';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuLabel,
} from './ui/dropdown-menu';
import {
  Home, Users, Trophy, Target, MapPin, Award, Settings, LogOut,
  Menu, X, Shield, Activity, Bell, Eye, Wrench, CreditCard, DollarSign, HelpCircle,
  Sun, Moon, Globe, User, BellRing, MoreHorizontal, BarChart3,
} from 'lucide-react';
import { getInitials } from '../lib/utils';
import { AdBanner } from './AdBanner';
import LoopingAdBanner from './LoopingAdBanner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3ec18717-9700-48f1-a5a2-71ac0edd3a6f/artifacts/29waugc4_WhatsApp%20Image%202026-03-06%20at%2016.26.44.jpeg';

export const Layout = ({ children }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [ghostMode, setGhostMode] = useState(() => localStorage.getItem('ghost-mode') || 'player');
  const { pushEnabled, pushSupported, enablePush, disablePush } = usePushNotifications(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchUnread = async () => {
      try {
        const res = await notificationsAPI.getUnreadCount();
        setUnreadCount(res.data?.count || 0);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const isAdmin = ['platform_admin', 'club_admin', 'venue_admin', 'tournament_admin'].includes(user?.role);
  const inManageMode = isAdmin && ghostMode === 'manage';

  const toggleGhostMode = () => {
    const newMode = ghostMode === 'player' ? 'manage' : 'player';
    setGhostMode(newMode);
    localStorage.setItem('ghost-mode', newMode);
  };

  const currentLang = LANGUAGES.find(l => l.code === lang);

  // Bottom nav items (max 5 for mobile)
  const bottomNavItems = isAuthenticated ? [
    { path: '/dashboard', label: 'Home', icon: Home },
    { path: '/matches', label: 'Matches', icon: Activity },
    { path: '/matches/new', label: 'Play', icon: Target, accent: true },
    { path: '/clubs', label: 'Clubs', icon: Shield },
    { path: '/_more', label: 'More', icon: MoreHorizontal },
  ] : [
    { path: '/', label: 'Quick Play', icon: Target, accent: true },
    { path: '/tournament', label: 'Tournament', icon: Trophy },
    { path: '/streaks', label: 'My Streaks', icon: Activity },
    { path: '/rivals', label: 'Rivals', icon: Users },
  ];

  const getNavLinks = () => {
    if (!isAuthenticated) {
      return [
        { path: '/', label: 'Quick Play', icon: Target },
        { path: '/tournament', label: 'Tournament', icon: Trophy },
        { path: '/streaks', label: 'My Streaks', icon: Activity },
        { path: '/rivals', label: 'Rivals', icon: Users },
      ];
    }
    const base = [
      { path: '/dashboard', label: t('nav.dashboard'), icon: Home },
      { path: '/matches', label: t('nav.matches'), icon: Activity },
      { path: '/players', label: t('nav.players'), icon: Users },
      { path: '/clubs', label: t('nav.clubs'), icon: Shield },
      { path: '/teams', label: 'Teams', icon: Users },
      { path: '/tournaments', label: t('nav.tournaments'), icon: Trophy },
    ];
    if (isAdmin && inManageMode) {
      base.push({ path: '/analytics', label: 'Analytics', icon: BarChart3 });
      base.push({ path: '/revenue', label: 'Revenue', icon: DollarSign });
      base.push({ path: '/admin', label: t('nav.admin'), icon: Settings });
    }
    return base;
  };

  const navLinks = getNavLinks();
  const isActive = (path) => location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path + '/'));
  const isHomePage = location.pathname === '/' || location.pathname === '/dashboard';

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 bg-card/95 backdrop-blur-lg border-b border-border safe-top" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo */}
            <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-2 shrink-0">
              <img src={LOGO_URL} alt="LoveAll" className="h-12 w-12 rounded-full object-cover" />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map(link => {
                const Icon = link.icon;
                const active = isActive(link.path);
                return (
                  <Link key={link.path} to={link.path}>
                    <Button variant={active ? 'secondary' : 'ghost'} size="sm" className={`h-8 text-xs ${active ? 'bg-primary/10 text-primary' : ''}`}>
                      <Icon className="h-3.5 w-3.5 mr-1" />{link.label}
                    </Button>
                  </Link>
                );
              })}
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Notification bell - mobile */}
              {isAuthenticated && (
                <Link to="/notifications" className="relative sm:hidden">
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </Link>
              )}

              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0" data-testid="user-menu-trigger">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user?.picture} />
                        <AvatarFallback className="bg-primary/20 text-primary text-xs">{getInitials(user?.name)}</AvatarFallback>
                      </Avatar>
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold hidden sm:flex">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                      {user?.role && <Badge variant="outline" className="mt-1 text-[10px] capitalize">{user.role.replace('_', ' ')}</Badge>}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-my-profile">
                      <User className="mr-2 h-4 w-4" />{t('nav.myProfile')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/notifications')} data-testid="menu-notifications">
                      <Bell className="mr-2 h-4 w-4" />{t('nav.notifications')}
                      {unreadCount > 0 && <Badge className="ml-auto bg-red-500 text-[10px]">{unreadCount}</Badge>}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/support')} data-testid="menu-support">
                      <HelpCircle className="mr-2 h-4 w-4" />Support
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider">Settings</DropdownMenuLabel>
                    {isAdmin && (
                      <DropdownMenuItem onClick={toggleGhostMode} data-testid="menu-ghost-mode">
                        {inManageMode ? <Wrench className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
                        {inManageMode ? 'Admin Mode' : 'Player Mode'}
                        <Badge variant="outline" className="ml-auto text-[10px]">{inManageMode ? 'Manage' : 'View'}</Badge>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={toggleTheme} data-testid="menu-theme-toggle">
                      {theme === 'dark' ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                      {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                    </DropdownMenuItem>
                    {pushSupported && (
                      <DropdownMenuItem
                        onClick={async () => {
                          if (pushEnabled) { await disablePush(); } else {
                            const ok = await enablePush();
                            if (!ok) { const { toast } = await import('sonner'); toast.error('Could not enable notifications.'); }
                          }
                        }}
                        data-testid="menu-push-toggle"
                      >
                        <BellRing className="mr-2 h-4 w-4" />Push Notifications
                        <Badge variant="outline" className="ml-auto text-[10px]">{pushEnabled ? 'ON' : 'OFF'}</Badge>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger data-testid="menu-language">
                        <Globe className="mr-2 h-4 w-4" />{currentLang?.nativeName || 'Language'}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {LANGUAGES.map(l => (
                          <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} className={lang === l.code ? 'bg-primary/10 text-primary' : ''} data-testid={`lang-${l.code}`}>
                            {l.nativeName}<span className="ml-auto text-xs text-muted-foreground">{l.code.toUpperCase()}</span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive" data-testid="menu-logout">
                      <LogOut className="mr-2 h-4 w-4" />{t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/owner-login">
                  <Button size="sm" variant="ghost" className="h-8 text-[11px] text-muted-foreground hover:text-foreground" data-testid="layout-login-btn">Owner Sign In</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile "More" drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="absolute bottom-16 left-0 right-0 bg-card border-t border-border rounded-t-2xl p-4 pb-6 safe-bottom animate-in slide-in-from-bottom" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mb-4" />
            <div className="grid grid-cols-4 gap-3">
              {navLinks.filter(l => !bottomNavItems.slice(0, 4).some(b => b.path === l.path)).map(link => {
                const Icon = link.icon;
                const active = isActive(link.path);
                return (
                  <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-colors ${active ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}>
                    <Icon className="h-5 w-5" />
                    <span className="text-[10px] font-medium">{link.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Page Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 pb-28 lg:pb-6">
        <div className="flex gap-6">
          {/* Main content area */}
          <main className="flex-1 min-w-0">
            {children}
          </main>

          {/* Desktop sidebar ad (right side) — hidden on home/dashboard.
              Uses LoopingAdBanner so the same admin-configured campaign that
              appears on Quick Play also shows up on Tournaments and other
              secondary pages (single source of truth = /api/telemetry/ad-config-public). */}
          {!isHomePage && (
            <aside className="hidden lg:block w-[220px] shrink-0 sticky top-20 self-start" data-testid="desktop-ad-sidebar">
              <LoopingAdBanner variant="inline" />
            </aside>
          )}
        </div>
      </div>

      {/* Mobile sticky ad — above bottom nav, hidden on home/dashboard.
          Mirrors the same admin-configured ad that's shown on the home page. */}
      {!isHomePage && (
        <div className="fixed bottom-14 left-0 right-0 z-30 px-2 pb-1 lg:hidden" data-testid="mobile-ad-strip">
          <LoopingAdBanner variant="strip" />
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-lg border-t border-border lg:hidden safe-bottom" data-testid="bottom-nav">
        <div className="flex items-center justify-around px-1 h-14">
          {bottomNavItems.map(item => {
            const Icon = item.icon;
            const isMore = item.path === '/_more';
            const active = isMore ? mobileMenuOpen : isActive(item.path);
            
            if (isMore) {
              return (
                <button
                  key={item.path}
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${active ? 'text-primary' : 'text-muted-foreground'}`}
                  data-testid="bottom-nav-more"
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </button>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                  item.accent ? '' : active ? 'text-primary' : 'text-muted-foreground'
                }`}
                data-testid={`bottom-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                {item.accent ? (
                  <div className="bg-secondary text-secondary-foreground rounded-full p-2 -mt-4 shadow-lg border-4 border-background">
                    <Icon className="h-5 w-5" />
                  </div>
                ) : (
                  <>
                    <Icon className="h-5 w-5" />
                    <span className="text-[10px] font-medium">{item.label}</span>
                  </>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Layout;
